import { useCallback, useMemo } from 'react';

import { TRendererCallbackMap } from '@ldp/view-resolver-lite';
import { useAnalyticsContext, useFeatureFlags } from '@ldp/common-contexts';
import { SnowplowAdEvents, trackProductTilesRenderAdEvent, trackRootAdRenderEvent } from '@ldp/media-analytics';
import {
  SDMAnalyticEvents,
  trackAnalyticsRenderEvents,
  trackBentoCardEvents,
  trackBrandSpotlightComponentEvents,
  trackProductCarouselEvents,
} from '@ldp/sdm-analytics-utils';
// import { TViewRenderer, TViewRendererData } from "../../utils/view-resolver-utils";

/**
 * Hook to memoize the renderer callback map.
 */
export const useRendererCallbackMap = () => {
  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow } = getFeatureFlags();

  // Transform function that handles BFF data structure properly
  // BFF returns components with data already nested under .data property
  // We need to preserve this structure, not double-nest it
  const transformComponentData = (componentData: any): any => {
    const { componentId, contentId, id, breakpoint, ...rest } = componentData;
    
    // If the component already has a 'data' property from the BFF, use it directly
    // Otherwise, nest the remaining properties under 'data'
    const data = rest.data !== undefined ? rest.data : rest;
    
    return {
      componentId,
      contentId,
      id,
      breakpoint,
      data,
    };
  };

  // Wrapper over the media analytics track event function for root ads
  const trackRootAdRenderCallback = useCallback(
    (event: SnowplowAdEvents, componentData: any) => {
      trackRootAdRenderEvent(
        event,
        !!EnableHeliosSnowplow,
        transformComponentData(componentData),
        new Map<string, boolean>(),
        trackEvent
      );
    },
    [trackEvent, EnableHeliosSnowplow]
  );

  // Wrapper over the media analytics track event function for root ads
  // Delay media next gen components by 1 second to fire ad view
  const trackMediaAdViewCallback = useCallback(
    (componentData: any) => {
      setTimeout(() => {
        trackRootAdRenderEvent(
          SnowplowAdEvents.AD_VIEW,
          !!EnableHeliosSnowplow,
          transformComponentData(componentData),
          new Map<string, boolean>(),
          trackEvent
        );
      }, 1000);
    },
    [trackEvent, EnableHeliosSnowplow]
  );

  // Wrapper over the media analytics track event function for product tiles
  const trackProductTilesAdRenderCallback = useCallback(
    (
      event: SnowplowAdEvents,
      componentData: any,
      dataPaths: {
        productTiles: string;
        pagination?: string | undefined;
      }
    ) => {
      trackProductTilesRenderAdEvent(
        event,
        !!EnableHeliosSnowplow,
        transformComponentData(componentData),
        dataPaths,
        new Map<string, boolean>(),
        trackEvent
      );
    },
    [trackEvent, EnableHeliosSnowplow]
  );

  // Wrapper over the media analytics track event function for bentocard
  // BFF returns bento cards with data nested under .data property
  // trackBentoCardEvents expects bentoCards at top level, so we need to flatten
  const trackBentoCardLoadRenderCallback = useCallback(
    (
      event: SDMAnalyticEvents,
      componentData: any,
      dataPaths: {
        productTiles: string;
        pagination?: string | undefined;
      }
    ) => {
      // Flatten the data structure if BFF has nested data under .data
      const flattenedData = componentData?.data
        ? { ...componentData, ...componentData.data }
        : componentData;
      trackBentoCardEvents(event, !!EnableHeliosSnowplow, flattenedData, dataPaths, trackEvent);
    },
    [trackEvent, EnableHeliosSnowplow]
  );
  // Wrapper over the media analytics track event function for product tiles
  const trackProductCarouselLoadRenderCallback = useCallback(
    (
      event: SDMAnalyticEvents,
      componentData: any,
      dataPaths: {
        productTiles: string;
        pagination?: string | undefined;
      }
    ) => {
      trackProductCarouselEvents(
        event,
        !!EnableHeliosSnowplow,
        transformComponentData(componentData),
        dataPaths,
        trackEvent
      );
    },
    [trackEvent, EnableHeliosSnowplow]
  );

  /**
   * Callback function to track brand spotlight component events.
   * @param event The SDMAnalyticEvents event to track.
   * @param componentData The any component data.
   * @param dataPaths The data paths for product tiles and pagination.
   */
  const trackBrandSpotlightComponentLoadRenderCallback = useCallback(
    (
      event: SDMAnalyticEvents,
      componentData: any,
      dataPaths: {
        productTiles: string;
        pagination?: string | undefined;
      }
    ) => {
      trackBrandSpotlightComponentEvents(
        event,
        !!EnableHeliosSnowplow,
        transformComponentData(componentData),
        dataPaths,
        trackEvent
      );
    },
    [trackEvent, EnableHeliosSnowplow]
  );

  // Wrapper over the analytics track event function
  const trackComponentLoadRenderCallback = useCallback(
    (event: SDMAnalyticEvents, componentData: any) => {
      trackAnalyticsRenderEvents(event, !!EnableHeliosSnowplow, transformComponentData(componentData), trackEvent);
    },
    [trackEvent]
  );

  return useMemo<TRendererCallbackMap>(
    () => ({
      brandSpotlightComponent: {
        onLoad: (_, componentData) => {
          trackRootAdRenderCallback(SnowplowAdEvents.AD_LOAD, componentData);
          trackBrandSpotlightComponentLoadRenderCallback(SDMAnalyticEvents.LOAD, componentData, {
            productTiles: 'productTiles',
          });
        },
        onView: (_, componentData) => {
          trackRootAdRenderCallback(SnowplowAdEvents.AD_VIEW, componentData);
          trackComponentLoadRenderCallback(SDMAnalyticEvents.COMPONENT_VIEW, componentData);
          trackBrandSpotlightComponentLoadRenderCallback(SDMAnalyticEvents.VIEW, componentData, {
            productTiles: 'productTiles',
          });
        },
      },
      productListingComponent: {
        onLoad: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_LOAD, componentData, {
            productTiles: 'productGrid.productTiles',
            pagination: 'productGrid.pagination',
          });
        },
        onView: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_VIEW, componentData, {
            productTiles: 'productGrid.productTiles',
            pagination: 'productGrid.pagination',
          });
        },
      },
      productCarouselComponent: {
        onLoad: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_LOAD, componentData, {
            productTiles: 'productTiles',
          });
          trackProductCarouselLoadRenderCallback(SDMAnalyticEvents.LOAD, componentData, {
            productTiles: 'productTiles',
          });
        },
        onView: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_VIEW, componentData, {
            productTiles: 'productTiles',
          });
          trackProductCarouselLoadRenderCallback(SDMAnalyticEvents.VIEW, componentData, {
            productTiles: 'productTiles',
          });
        },
      },
      bentoCardContainerComponent: {
        onLoad: (_, componentData) => {
          trackBentoCardLoadRenderCallback(SDMAnalyticEvents.LOAD, componentData, {
            productTiles: 'productTiles',
          });
        },
        onView: (_, componentData) => {
          trackBentoCardLoadRenderCallback(SDMAnalyticEvents.VIEW, componentData, {
            productTiles: 'productTiles',
          });
        },
      },
      productGridContainer: {
        onLoad: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_LOAD, componentData, {
            productTiles: 'productGrid.productTiles',
            pagination: 'productGrid.pagination',
          });
        },
        onView: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_VIEW, componentData, {
            productTiles: 'productGrid.productTiles',
            pagination: 'productGrid.pagination',
          });
        },
      },
      productGridComponent: {
        onLoad: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_LOAD, componentData, {
            productTiles: 'productTiles',
            pagination: 'pagination',
          });
        },
        onView: (_, componentData) => {
          trackProductTilesAdRenderCallback(SnowplowAdEvents.AD_VIEW, componentData, {
            productTiles: 'productTiles',
            pagination: 'pagination',
          });
        },
      },
      verticalFeatureCardContainerComponent: {
        onView: (_, componentData) => {
          trackComponentLoadRenderCallback(SDMAnalyticEvents.COMPONENT_VIEW, componentData);
        },
      },
      horizontalFeatureCardContainerComponent: {
        onView: (_, componentData) => {
          trackComponentLoadRenderCallback(SDMAnalyticEvents.COMPONENT_VIEW, componentData);
        },
      },
      menuTileCarouselComponent: {
        onView: (_, componentData) => {
          trackComponentLoadRenderCallback(SDMAnalyticEvents.COMPONENT_VIEW, componentData);
        },
      },
      bannerWithHeadlineComponent: {
        onView: (_, componentData) => {
          trackComponentLoadRenderCallback(SDMAnalyticEvents.COMPONENT_VIEW, componentData);
        },
      },
      mediaIABComponent: {
        onLoad: (_, componentData) => {
          trackRootAdRenderCallback(SnowplowAdEvents.AD_LOAD, componentData);
        },
        onView: (_, componentData) => {
          trackMediaAdViewCallback(componentData);
        },
      },
      mediaShoppableDisplayComponent: {
        onLoad: (_, componentData) => {
          trackRootAdRenderCallback(SnowplowAdEvents.AD_LOAD, componentData);
        },
        onView: (_, componentData) => {
          trackMediaAdViewCallback(componentData);
        },
      },
    }),
    [trackEvent]
  );
};

export default useRendererCallbackMap;
